import React, { useEffect, useState } from "react";
import "~/css/QRSetting.css";
import "~/css/Button.css";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { PlaySound } from "~/SoundUtils";
import { volumeAtom } from "~/Atom";
import { qrCodeTitleAtom, qrCodeUrlAtom } from "~/AtomWithDynamoDB";
import { QRCodeSVG } from "qrcode.react";
import { showNotificationDialog } from "~/utils/notificationDialogUtils";

const InputTextForm: React.FC<{
  inputText: string;
  setInputText: (value: string) => void;
  label: string;
}> = ({ inputText, setInputText, label }) => {
  return (
    <div className="qr-input-row">
      <span className="qr-input-label">{label}</span>
      <div className="qr-input-content">
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder={label}
        />
      </div>
    </div>
  );
};

/**
 * 二次元コード設定コンポーネント(アコーディオン用)
 * @returns 二次元コード設定コンポーネントのJSX要素
 */
const QRCodeSettings: React.FC = () => {
  const { t } = useTranslation();
  const [volume] = useAtom(volumeAtom);
  const [qrCodeURL, setQrCodeURL] = useAtom(qrCodeUrlAtom);
  const [qrCodeTitle, setQrCodeTitle] = useAtom(qrCodeTitleAtom);
  const [inputURL, setInputURL] = useState<string>("");
  const [inputTitle, setInputTitle] = useState<string>("");
  const [qrSize, setQrSize] = useState<number>(200);

  // デバイスの向きを検知
  useEffect(() => {
    const checkOrientation = () => {
      const containerWidth = window.innerWidth;
      const containerHeight = window.innerHeight;

      if (window.innerHeight > window.innerWidth) {
        setQrSize(containerWidth * 0.3);
      } else {
        setQrSize(containerHeight * 0.3);
      }
    };
    checkOrientation();

    window.addEventListener("resize", checkOrientation);
    window.addEventListener("orientationchange", checkOrientation);

    return () => {
      window.removeEventListener("resize", checkOrientation);
      window.removeEventListener("orientationchange", checkOrientation);
    };
  }, []);

  // Atomから二次元コードURL、タイトルを取得して入力フォームに反映
  useEffect(() => {
    if (qrCodeURL && qrCodeTitle) {
      setInputURL(qrCodeURL);
      setInputTitle(qrCodeTitle);
    }
  }, [qrCodeURL, qrCodeTitle]);

  /**
   * 登録ボタンがクリックされた時の処理
   * @returns void
   */
  const handleRegisterClick = () => {
    setQrCodeURL(inputURL);
    setQrCodeTitle(inputTitle);
    showNotificationDialog(t("saved"), "success");
    PlaySound("push", volume);
  };

  /**
   * 二次元コードが登録されているかどうかを確認
   * @returns 登録されていればtrue、されていなければfalse
   */
  const isQrCodeRegistered = () => {
    return Boolean(qrCodeURL && qrCodeTitle);
  };

  return (
    <div className="qr-code-settings">
      <div className="qr-code-content">
        <div className="qr-code-display">
          {isQrCodeRegistered() ? (
            <QRCodeSVG value={qrCodeURL} size={qrSize} />
          ) : (
            <h4>{t("qrCodeNotRegister")}</h4>
          )}
        </div>
        <div className="qr-input-section">
          <InputTextForm inputText={inputTitle} setInputText={setInputTitle} label={t("title")} />
          <InputTextForm inputText={inputURL} setInputText={setInputURL} label={t("URL")} />
        </div>
      </div>

      <div className="button-container">
        <button
          onClick={handleRegisterClick}
          className="button-1 flexible"
          disabled={!inputURL || !inputTitle}
        >
          {t("register")}
        </button>
      </div>
    </div>
  );
};

export default QRCodeSettings;
